/**
 * next/navigation → TanStack Router compat. Vite aliases the bare specifier
 * "next/navigation" here, so existing components keep their imports unchanged.
 * Use idiomatic TanStack APIs in NEW code.
 */
import {
  useLocation,
  useNavigate,
  useParams as useTsxParams,
  useRouter as useTsxRouter,
  notFound as tsrNotFound,
} from "@tanstack/react-router"
import { useMemo } from "react"
import { getAppRouter } from "../router-ref"

export interface AppRouter {
  push: (href: string, options?: { scroll?: boolean }) => void
  replace: (href: string, options?: { scroll?: boolean }) => void
  back: () => void
  forward: () => void
  refresh: () => void
  prefetch: (href?: string) => void
}

export function useRouter(): AppRouter {
  const navigate = useNavigate()
  const router = useTsxRouter()
  return useMemo<AppRouter>(
    () => ({
      push: (href, options) =>
        void navigate({ href, resetScroll: options?.scroll !== false } as never),
      replace: (href, options) =>
        void navigate({ href, replace: true, resetScroll: options?.scroll !== false } as never),
      back: () => router.history.back(),
      forward: () => router.history.forward(),
      // Next's refresh() re-runs server data — nearest equivalent is
      // invalidating the router (loaders) + TanStack Query via router context.
      refresh: () => void router.invalidate(),
      prefetch: (href) => {
        if (href) void router.preloadRoute({ to: href } as never).catch(() => {})
      },
    }),
    [navigate, router],
  )
}

export function usePathname(): string {
  return useLocation({ select: (l) => l.pathname })
}

/** Matches Next's ReadonlyURLSearchParams surface (.get/.getAll/.has/iteration). */
export function useSearchParams(): URLSearchParams {
  const searchStr = useLocation({ select: (l) => l.searchStr })
  return useMemo(() => new URLSearchParams(searchStr), [searchStr])
}

export function useParams<T extends Record<string, string> = Record<string, string>>(): T {
  return useTsxParams({ strict: false } as never) as T
}

const REDIRECT_SENTINEL = Symbol.for("vucar.spa.redirect")

/** Client-side redirect(): navigate off-render then halt the render pass. */
export function redirect(url: string): never {
  queueMicrotask(() => {
    getAppRouter()?.navigate({ href: url } as never)
  })
  throw REDIRECT_SENTINEL
}

export function permanentRedirect(url: string): never {
  redirect(url)
}

export function isRedirectSentinel(err: unknown): boolean {
  return err === REDIRECT_SENTINEL
}

export function notFound(): never {
  throw tsrNotFound()
}
